import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import {createRef, ref} from 'lit/directives/ref.js';
import {listen} from 'nuffjs';

import store from '../../../store';

import {pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';
import {
	selectSpcConfiguration, selectLanguage
} from '../../../selectors';
import {
	selectGroupedHeads, selectOlivioBase,
	selectOlivioBaseDescription,
	selectOlivioBaseNumber,
	selectOlivioBracketDescription,
	selectOlivioBracketNumber, selectOlivioBuriedBaseSize,
	selectOlivioCandelabraTopLabel,
	selectOlivioDesign,
	selectOlivioDesignIsCandelabra,
	selectOlivioDesignIsFloracion,
	selectOlivioDesignIsSistema,
	selectOlivioDesignLabel,
	selectOlivioDiameter, selectOlivioFuseBox, selectOlivioFuseBoxDescription, selectOlivioFuseBoxNumber,
	selectOlivioHeads,
	selectOlivioHousingColourInlay,
	selectOlivioHousingColourPole,
	selectOlivioNumberOfHeads,
	selectOlivioPoleDescription,
	selectOlivioPoleDiameter,
	selectOlivioPoleHeight,
	selectOlivioPoleNumber, selectOlivioSecondDoor, selectOlivioSecondDoorDescription,
	selectOlivioSecondDoorLabel,
	selectOlivioSeparateBuriedBase,
	selectOlivioSeparateBuriedBaseDescription,
	selectOlivioSeparateBuriedBaseHeight
} from '../../olivioSelectors';
import {saveTenderText} from '../../../slices/configurationSlice';

@customElement('olivio-tender-text')
export class OlivioTenderText extends connect(store)(LitElement) {

	static styles = css`
		:host {
			container-type: inline-size;
			font: var(--font-specsheet-print-small-text);
		}

		p {
			margin: 0 0 1cqw 0;
			font: var(--font-specsheet-print-small-text);
			font-size: 1.4cqw;
			line-height: 140%;
		}
		.info {
			font: var(--font-specsheet-print-small-text);
			font-size: 1cqw;
		}
	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	tenderTextRef = createRef();

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.numberOfHeads = selectOlivioNumberOfHeads(state);
		this.heads = selectOlivioHeads(state).filter(h => h.active);
		this.groupedHeads = selectGroupedHeads(state);
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}
	firstUpdated(changedProperties) {
		const tenderText = this.tenderTextRef.value.innerText;
		const cleanedText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		store.dispatch(saveTenderText({tenderText: cleanedText}));
	}

	getSpcInstanceIdForHead(index) {
		return this.groupedHeads.find(headGroup => headGroup.indexes.includes(index)).instanceId;
	}

	getHeadText(headData) {
		return html`
			${msg('Head')} ${headData.indexes.map(index => index + 1).join(', ')}:<br>
			<olivio-tender-text-head-data .headData="${headData}"></olivio-tender-text-head-data>
		`;
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}
	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;
		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		navigator.clipboard.writeText(tenderText).then(
			() => {},
			(err) => {
				console.error('Could not copy text: ', err);
			});
	}

	render() {
		const s = this.state;

		return html`
			<div ${ref(this.tenderTextRef)}>
				<p class="headline">${selectOlivioDesignLabel(s)} ${msg('Luminaire Configuration')}</p>
				<p>
					<trans-label key="LU_LuminaireDescription" context="D.L"></trans-label>
				</p>
				<p>
					${msg('consisting of')}<br><br>

					${repeat(this.groupedHeads,
						(headData, index) => 'head_' + index,
						(headData) => this.getHeadText(headData))}
					<br>
					${this.t('HS_HousingDescription', 'D.L')}, ${msg('connected supply line')},
					${this.t('LU_Maintanance', 'D.L', null, '', '')}
					<br><br>
				</p>

				<p>
					${msg('Pole')}:<br>
					${selectOlivioPoleDescription(s)}, ${msg('Height')} ${selectOlivioPoleHeight(s)} m,
					${msg('Diameter')} (${msg('top')}) ${selectOlivioDiameter(s)} mm${when(selectOlivioDesignIsCandelabra(s), () => html`, ${selectOlivioCandelabraTopLabel(s)}`)}<br>
					${when(selectOlivioSecondDoor(s) !== 'none', () => html`${selectOlivioSecondDoorLabel(s)}, ${selectOlivioSecondDoorDescription(s)}<br>`)}
					${selectOlivioPoleNumber(s)}<br><br>
					${when(selectOlivioFuseBox(s) !== 'none', () => html`${selectOlivioFuseBoxDescription(s)}, ${selectOlivioFuseBoxNumber(s)}<br>`)}<br>
				</p>

				${when(selectOlivioDesignIsSistema(s), () => html`
					<p>
						${msg('Bracket')}:<br>
						${this.numberOfHeads} ${selectOlivioBracketDescription(s)}<br>
						${selectOlivioBracketNumber(s)}
						<br><br>
					</p>
				`)}

				<p>
					<!-- HS_Base -->
					${this.t('HS_Base', 'F')}:<br>
					${selectOlivioBaseDescription(s)},
					${when( selectOlivioBase(s) === 'buriedbase', () => html`${selectOlivioBuriedBaseSize(s)} mm`)}
				 	<!--, ${selectOlivioBaseNumber(s)}--><br>
					${when(selectOlivioSeparateBuriedBase(s) !== 'none', () => html`${selectOlivioSeparateBuriedBaseDescription(s)}, ${selectOlivioSeparateBuriedBaseHeight(s)}mm<br>`)}
				</p>

				<p>
					<!-- HS_HousingColour -->
					${this.t('HS_HousingColour', 'F', null, '', ':')}<br>
					${when(!selectOlivioDesignIsFloracion(s),
						() => html`
							${this.t('HS_HousingColour', 'FV')}
						`, () => html`
							${msg('Luminaire')}: ${this.t('HS_HousingColour', 'FV')},
							${msg('Pole')}: ${this.t('HS_HousingColour', 'FV', selectOlivioHousingColourPole(s))},
							${msg('Inlay')}: ${this.t('HS_HousingColour', 'FV', selectOlivioHousingColourInlay(s))}
						`)
					}
				</p>

				<p>
					<!-- EB_ControlNoWire_1 EB_ControlWire_1-->
					${this.t('EB_ControlNoWire_1', 'F', null, '', ':')}
					${this.t('EB_ControlNoWire_1', 'F', null, '', ':')}
					${when(this.t('EB_Control_1', 'FV.S'),
						() => html`${this.t('EB_Control_1', 'FV.S')}`,
						() => html`
							${filterAndJoin([
							this.t('EB_ControlNoWire_1', 'FV.S'),
							this.t('EB_ControlWire_1', 'FV.S'),
						], ' + ')}
						`)
					}
					<br>

					<!-- EB_ConstantLumenTechnology_1 -->
					${this.t('EB_ConstantLumenTechnology_1', 'F', null, '', ':')}
					${this.t('EB_ConstantLumenTechnology_1', 'FV.S')}
					<br>

					<!-- EB_Zhaga_1 -->
					${this.t('EB_Zhaga_1', 'F', null, '', ':')}
					${this.t('EB_Zhaga_1', 'FV.L')}
					<br>

					<!-- HS_HousingCoating -->
					${this.t('HS_HousingCoating', 'F', null, '', ':')}
					${this.t('HS_HousingCoating', 'FV')}
					<br>

					<!-- LU_Warranty -->
					${this.t('LU_Warranty', 'D', null, '', ':')}
					${this.t('LU_Warranty', 'D.L', null, '', '')}

					<br><br>
					${this.t('TE_Manufacturer', 'D', null, '', ':')} Selux
				</p>

			</div>
		`;
	}
}
